import { cloneDeep, endsWith, range, startsWith } from 'lodash-es';

import Field from './Field';

// Lazy import to avoid circular dependency - components import models which import this file
let componentsCache: Record<string, React.ComponentType<unknown>> | null = null;
function getComponents(): Record<string, React.ComponentType<unknown>> {
  if (!componentsCache) {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    componentsCache = require('../components/Sections');
  }
  return componentsCache;
}
import type { Mandate } from '../components/Widgets/MultiMandate';
import type { ValidationType } from '../utils/validators';
import type { SubmittedData } from '@zg-rentals/ts-leasing-client';

interface SectionCategory {
  key: string;
  label: string;
}

interface FieldJSON {
  fieldName: string;
  dataType?: string;
  optional?: boolean;
  validation?: ValidationType;
  defaultValue?: string | number | boolean | null;
  enteredValue?: string | number | boolean | null;
  disabled?: boolean;
}

interface SectionParams {
  currentNodeId?: number;
  hasVisited?: boolean;
  hasNext?: boolean;
  hasPrevious?: boolean;
  mandateTitle?: string;
  mandateText?: string;
  sectionCategory?: string;
  status?: string;
  uiSection?: string;
  mandates?: Array<Mandate>;
  leaseMandates?: Array<Mandate>;
  fields?: Array<FieldJSON>;
}

// Section category definitions
export const SECTION_CATEGORIES: Array<SectionCategory> = [
  { key: 'partiesToTheLease', label: `Parties to the lease` },
  { key: 'propertyInfo', label: 'Property and amenities' },
  { key: 'termsAndPayment', label: 'Terms and payment' },
  { key: 'rightsAndObligations', label: 'Rights and obligations' },
  { key: 'additionalDetails', label: 'Additional details' },
];

/**
 * Class to model section data returned by API
 */
export default class Section {
  currentNodeId: number;

  readonly fieldMappings: { [s: string]: Field };

  hasVisited: boolean;

  hasNext: boolean;

  hasPrevious: boolean;

  private readonly indexedFields: { [s: string]: Array<Field> };

  initialParams: SectionParams;

  mandateText: string;

  mandateTitle: string;

  mandates: Array<Mandate>;

  maxIndexedFieldsThatCanBeAdded: number;

  showMandate: boolean;

  sectionCategory: string;

  status: string;

  uiSection: string;

  // This is to be overriden with Field definitions where applicable
  indexedFieldTypes: Array<Field>;

  constructor(params) {
    this.currentNodeId = params.currentNodeId;
    this.fieldMappings = {};
    this.hasVisited = params.hasVisited;
    this.hasNext = params.hasNext;
    this.hasPrevious = params.hasPrevious;
    this.indexedFields = {};
    this.initialParams = params;
    this.mandateTitle = params.mandateTitle;
    this.mandateText = params.mandateText;
    this.showMandate = true;
    this.sectionCategory = params.sectionCategory;
    this.status = params.status;
    this.uiSection = params.uiSection;
    this.mandates = params.mandates || params.leaseMandates || [];

    for (const fieldJSON of params.fields || []) {
      const field = new Field(fieldJSON);
      const { index } = field.getFieldNameRootAndIndex();

      // indexedFields are fields with indexes on them. e.g.: tenant0_firstName, pet0_gender, etc...
      if (index == null) {
        this.addFieldMapping(field);
      } else {
        const isEnabledField = endsWith(field.fieldName, 'isEnabled');

        if (
          (!isEnabledField && field.enteredValue !== '' && field.enteredValue != null) ||
          (isEnabledField && field.enteredValue === true)
        ) {
          // Note we're passing the original fieldJSON rather than the field to avoid doubly-processing
          // enteredValues (e.g.: currency)
          this.addIndexedField(fieldJSON, index);
        }
      }
    }
  }

  // The user may have persisted incomplete data. Create placeholder fields for the incomplete data
  private initializeMissingIndexedFields() {
    range(0, this.getTotalIndexes() || 0).forEach((index: number) => {
      this.indexedFieldTypes.forEach((field: Field) => {
        const indexedFieldName = Field.getIndexedFieldName(field.fieldName, index);
        if (!(indexedFieldName in this.fieldMappings)) {
          this.addIndexedField(field, index);
        }
      });
    });
  }

  /**
   * Adds fields that there can be multiple of, e.g.: tenant0_email, tenant1_email, tenant2_email, etc...
   * @param indexedFieldTypes
   */
  setIndexedFieldTypes(indexedFieldTypes: Array<Field>) {
    const indexedFieldGroupingsCount = {};
    const indexedFieldsAsDefinedInDb = new Set(
      this.initialParams.fields.map((field) => {
        const { index, fieldNameRoot } = Field.getFieldNameRootAndIndex(field.fieldName);
        indexedFieldGroupingsCount[fieldNameRoot] = (indexedFieldGroupingsCount[fieldNameRoot] || 0) + 1;

        return index == null ? null : fieldNameRoot;
      }),
    );

    const fieldsThatMatchWhatsDefinedInDb = indexedFieldTypes.filter((field) => {
      const { fieldNameRoot } = field.getFieldNameRootAndIndex();

      return indexedFieldsAsDefinedInDb.has(fieldNameRoot);
    });

    const firstIndexedField = Object.keys(indexedFieldGroupingsCount)[0];
    this.maxIndexedFieldsThatCanBeAdded = indexedFieldGroupingsCount[firstIndexedField];
    this.indexedFieldTypes = fieldsThatMatchWhatsDefinedInDb;
    this.initializeMissingIndexedFields();
  }

  addFieldMapping = (field: Field) => (this.fieldMappings[field.fieldName] = field);

  addMandate = (mandate: Mandate) => {
    this.mandates.push(mandate);
  };

  addIndexedField = (originalField: Field, targetIndex?: number, overWriteExisting = true) => {
    const field = new Field(originalField);
    const { fieldNameRoot } = field.getFieldNameRootAndIndex();
    const indexedFieldGroup = this.indexedFields[fieldNameRoot] || [];
    const index = targetIndex != null ? targetIndex : indexedFieldGroup.length;

    field.fieldName = Field.getIndexedFieldName(fieldNameRoot, index);
    if (overWriteExisting || !(field.fieldName in this.fieldMappings)) {
      indexedFieldGroup[index] = field;
      this.indexedFields[fieldNameRoot] = indexedFieldGroup;
      this.addFieldMapping(field);
    }
  };

  /**
   * Returns a React component representing this Section
   * @returns {*}
   */
  getComponent = () => {
    const components = getComponents();
    const SectionComponent = components[this.uiSection];

    if (SectionComponent == null) {
      /*
      This placeholder component is necessary so that as multiple people are working on new sections,
      if one of us adds a new section to the API (along with a component), those of us without
      the new component aren't stuck in a broken limbo state if the getLeaseStatus takes us to the new section.

      This should never happen in production.
       */
      const errorMsg = `There is no component associated with section "${this.uiSection}"`;
      // eslint-disable-next-line no-console
      console.error(errorMsg);
    }

    return SectionComponent;
  };

  getField = (fieldName: string): Field => {
    const field = this.fieldMappings[fieldName];

    return field;
  };

  getFieldErrorMessage = (fieldName: string): string => {
    const field = this.getField(fieldName);
    return field ? field.getErrorMessage() : null;
  };

  getIndexedFieldGroup = (indexedFieldName: string): Array<Field> => this.indexedFields[indexedFieldName];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getFieldValue = (fieldName: string): any => {
    const field = this.getField(fieldName);

    return field == null ? undefined : field.enteredValue;
  };

  isFieldDisabled = (fieldName: string): boolean => {
    const field = this.getField(fieldName);

    return field == null ? null : field.disabled;
  };

  getSectionCategoryIndex = (): number =>
    SECTION_CATEGORIES.findIndex((sectionCategory: SectionCategory) => sectionCategory.key === this.sectionCategory);

  hasField = (fieldName: string): boolean => fieldName in this.fieldMappings;

  hasFieldError = (fieldName: string): boolean => {
    const field = this.getField(fieldName);

    return field == null ? null : field.hasError;
  };

  hasErrors = (): boolean => {
    for (const fieldName in this.fieldMappings) {
      const field: Field = this.fieldMappings[fieldName];
      if (field.hasError) {
        return true;
      }
    }

    return false;
  };

  isSummary = (): boolean => {
    return this.uiSection === 'summary';
  };

  setCustomFieldValidator = (fieldName: string, valueIsValid: (value: string) => boolean) => {
    const field = this.getField(fieldName);

    field.customValidator = valueIsValid;
  };

  setValidation = (fieldName: string, validationType: ValidationType) => {
    const field = this.getField(fieldName);

    field.validation = validationType;
  };

  reindexFields = (fieldNameRoot: string) => {
    this.indexedFields[fieldNameRoot].forEach((fieldToUpdate: Field, newIndex: number) => {
      const newFieldName = Field.getIndexedFieldName(fieldNameRoot, newIndex);

      // update reference in fieldMappings
      delete this.fieldMappings[fieldToUpdate.fieldName];
      this.fieldMappings[newFieldName] = fieldToUpdate;

      fieldToUpdate.fieldName = newFieldName;
    });
  };

  removeField = (fieldName: string) => {
    const field: Field = this.fieldMappings[fieldName];

    if (field != null) {
      const { fieldNameRoot, index } = field.getFieldNameRootAndIndex();

      // Delete the field from this.fieldMappings
      delete this.fieldMappings[fieldName];

      // If this field is an indexed field, remove from the appropriate indexed field group
      // and re-order its siblings
      if (index != null) {
        this.indexedFields[fieldNameRoot].splice(index, 1);
        this.reindexFields(fieldNameRoot);
      }
    }
  };

  removeIndexedFieldByIndex = (field: Field, index: number) => {
    const { fieldNameRoot } = field.getFieldNameRootAndIndex();
    this.removeField(Field.getIndexedFieldName(fieldNameRoot, index));
  };

  removeIndexedFieldGroup = (indexedFieldName: string) => {
    const indexedField = this.indexedFields[indexedFieldName];

    if (indexedField) {
      for (const field of indexedField) {
        delete this.fieldMappings[field.fieldName];
      }
      delete this.indexedFields[indexedFieldName];
    }
  };

  /*
  If a field ends in 'isEnabled' and if its toggled to false, this function unsets all related fields
  with the same prefix.

  e.g.:
    Setting pets_isEnabled = false removes fields named pets_pet0_fieldName, pets_pet1_fieldName AND...
    sets pets_insuranceRequired to default value
  */
  private unsetRelatedFields = (field: Field) => {
    const matches = field.fieldName.match(/(.*_)isEnabled$/);

    if (matches != null) {
      const prefix = matches[1];

      for (const fieldName in this.fieldMappings) {
        const currentField: Field = this.fieldMappings[fieldName];

        if (startsWith(fieldName, prefix)) {
          const { index } = currentField.getFieldNameRootAndIndex();

          /*
          If the related fields are indexed fields, remove them completely.
          Else just reset the fields to their default values
           */
          if (index != null) {
            this.removeField(currentField.fieldName);
          } else if (currentField !== field) {
            currentField.setEnteredValueToDefault();
          }
        }
      }
    }
  };

  /**
   * Returns a JSON representation of a section in a form that the API likes
   */
  getSubmitJSON = (): { data: Array<SubmittedData>; nodeId: number } => {
    const data = [];

    // The universe of possible fields for this section is described in the initialParams.fields.
    for (const fieldJSON of this.initialParams.fields) {
      const { fieldName } = fieldJSON;
      const field = this.fieldMappings[fieldName];

      if (fieldName.endsWith('isEnabled')) {
        if (field == null) {
          data.push({ sectionFieldName: fieldName, value: 'false' });
        } else if (field.enteredValue == null) {
          // If user doesn't click anything, don't populate for them.
          data.push({ sectionFieldName: fieldName, value: '' });
        } else if (!field.enteredValue) {
          data.push({ sectionFieldName: fieldName, value: 'false' });
        } else {
          data.push({ sectionFieldName: fieldName, value: 'true' });
        }
      } else if (field != null) {
        data.push({ sectionFieldName: fieldName, value: field.getJsonValue() });
      } else {
        data.push({ sectionFieldName: fieldName, value: '' });
      }
    }

    return { data, nodeId: this.currentNodeId };
  };

  setFieldValue = (fieldName: string, enteredValue: string | boolean) => {
    const field = this.getField(fieldName);

    if (field != null) {
      field.setEnteredValue(enteredValue);

      if (endsWith(fieldName, 'isEnabled') && enteredValue === false) {
        this.unsetRelatedFields(field);
      }
    }
  };

  setFieldValues = (fieldValues: object) => {
    for (const fieldName in fieldValues) {
      this.setFieldValue(fieldName, fieldValues[fieldName]);
    }
  };

  addOneIndex = () => {
    this.indexedFieldTypes.forEach((field: Field) => this.addIndexedField(field));
  };

  getTotalIndexes = () => {
    if (this.indexedFieldTypes == null) {
      return null;
    }

    const firstIndexedFieldType = this.indexedFieldTypes[0];
    let total = 0;

    if (firstIndexedFieldType) {
      const indexedFieldGroup = this.getIndexedFieldGroup(firstIndexedFieldType.fieldName) || [];
      total = indexedFieldGroup.length;
    }

    return total;
  };

  removeAllIndexes = () =>
    this.indexedFieldTypes.forEach((field: Field) => this.removeIndexedFieldGroup(field.fieldName));

  removeOneIndex = (index: number) =>
    this.indexedFieldTypes.forEach((field: Field) => this.removeIndexedFieldByIndex(field, index));

  hasChanged = (): boolean => {
    return Object.values(this.fieldMappings).some((field) => field.hasChanged());
  };

  clone = (): Section => {
    // TODO: Investigate using newer versions of immerjs in Landlord reducer to see if we can do away with cloning
    return cloneDeep(this);
  };
}
